import { callGeotabAPI } from '../api/geotab-client';
import { fetchRangeChunked } from '../api/fetch-range';
import { state } from '../state';
import type { ExceptionEvent, Rule, Group } from '../api/types';
import type { IdlingSettings, IdlingVehicleRaw, IdlingVehicleDecorated } from './idling-types';
import { L_PER_GAL, BURN_RATES_L, CURR_SYMBOLS } from './idling-types';
import { renderIdlingKPIs, renderIdlingCharts } from './idling-charts';
import { renderIdlingTable } from './idling-export';
import { buildGroupHierarchy, expandGroups, shallowestSelectedAncestor, isSystemGroupId } from '../api/group-hierarchy';
import type { GroupHierarchy } from '../api/group-hierarchy';
import { formatHms } from '../utils/time';

// ── Module state ──────────────────────────────────────────────────────────

export let idlingRawVehicleData: IdlingVehicleRaw[]       = [];
export let idlingRawEvents:      ExceptionEvent[]          = [];
export let idlingPerVehicle:     IdlingVehicleDecorated[] = [];
export let idlingDiscoveredRules: Rule[]                  = [];
// deviceId → matched Group label (same value as the table's Group column), so the Excel
// Event Detail sheet — which lists individual events, not the per-vehicle rollup — can show
// the same matched-filter group instead of state.allVehicles' arbitrary flattened one.
export let idlingGroupLabelByDevice: Map<string, string> = new Map();
export let idlingSortCol = 'cost';
export let idlingSortAsc = false;
let idlingInitialized = false;
let idlingGroupHierarchy: GroupHierarchy | null = null;

export function setIdlingSortCol(col: string): void { idlingSortCol = col; }
export function setIdlingSortAsc(asc: boolean): void { idlingSortAsc = asc; }

// ── Settings helpers ──────────────────────────────────────────────────────

export function getIdlingSettings(): IdlingSettings {
  const fpEl  = document.getElementById('idlingFuelPrice')  as HTMLInputElement  | null;
  const curEl = document.getElementById('idlingCurrency')   as HTMLSelectElement | null;
  const unEl  = document.getElementById('idlingUnit')       as HTMLSelectElement | null;
  const vtEl  = document.getElementById('idlingVehicleType') as HTMLSelectElement | null;
  return {
    fuelPrice: parseFloat(fpEl?.value ?? '4.50') || 0,
    currency:  curEl?.value ?? 'USD',
    unit:      (unEl?.value ?? 'gal') as 'L' | 'gal',
    burnRateL: BURN_RATES_L[vtEl?.value ?? 'medium'] ?? 3.5,
  };
}

export function volumeInUnit(idleHrs: number, burnRateL: number, unit: 'L' | 'gal'): number {
  const liters = idleHrs * burnRateL;
  return unit === 'gal' ? liters / L_PER_GAL : liters;
}

export function unitLabel(unit: 'L' | 'gal'): string {
  return unit === 'gal' ? 'gal' : 'L';
}

export function fmtHrs(h: number): string {
  return formatHms(h);
}

export function fmtCost(n: number, sym: string): string {
  return sym + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── TimeSpan parser ───────────────────────────────────────────────────────

export function parseTimespanToSec(val: string | number | null | undefined): number {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return Math.max(0, val);
  if (typeof val === 'string') {
    const parts = val.split(':');
    if (parts.length === 3) {
      const dh   = parts[0].split('.');
      const days  = dh.length > 1 ? parseInt(dh[0]) : 0;
      const hours = parseInt(dh[dh.length - 1]) || 0;
      const mins  = parseInt(parts[1]) || 0;
      const secs  = parseFloat(parts[2]) || 0;
      return days * 86400 + hours * 3600 + mins * 60 + secs;
    }
    const n = parseFloat(val);
    return isNaN(n) ? 0 : Math.max(0, n);
  }
  return 0;
}

// ── Threshold extraction from rule condition tree ─────────────────────────

export function extractThresholdFromRule(rule: Rule): number | null {
  try {
    const cond = rule.condition;
    if (!cond) return null;
    const walk = (node: typeof cond): number | null => {
      if (!node) return null;
      if (node.conditionType === 'Duration' && node.value !== undefined) {
        return parseTimespanToSec(node.value as string | number);
      }
      for (const child of node.children ?? []) {
        const r = walk(child);
        if (r !== null) return r;
      }
      return null;
    };
    return walk(cond);
  } catch { return null; }
}

export function fmtThreshold(sec: number | null): string {
  if (sec === null || sec === undefined) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s > 0 ? m + 'm ' + s + 's' : m + 'm';
}

// ── Page init ─────────────────────────────────────────────────────────────

export function initIdlingPage(): void {
  if (!idlingInitialized) {
    idlingSetRange('30d');
    idlingInitialized = true;
  }

  // Wiring runs BEFORE the data-dependent selector rendering below, and is wrapped so that
  // rendering can never prevent it: buildVehicleSelector()/populateGroupSelector() walk real
  // fleet data (device.groups, group hierarchies) that isn't guaranteed clean, and this whole
  // function has no caller-side try/catch (dashboard.ts's page-switch handler calls it inline,
  // with unrelated cleanup — stopping the Watchdog/Zone Activity refresh loops — right after).
  // An exception here used to run before the click listeners were attached, which permanently
  // wired nothing on that visit and every visit after — the Vehicle Group / Vehicles dropdowns
  // looked entirely dead, not just briefly empty.
  wireIdlingControlsOnce();

  try {
    if (state.allVehicles.length) buildVehicleSelector();
  } catch (e) {
    console.error('buildVehicleSelector failed', e);
  }
  try {
    if (state.allVehicles.length && idlingGroupHierarchy) populateGroupSelector();
  } catch (e) {
    console.error('populateGroupSelector failed', e);
  }
}

function wireIdlingControlsOnce(): void {
  const runBtn = document.getElementById('runIdlingBtn') as HTMLButtonElement & { _wired?: boolean };
  if (runBtn._wired) return;
  runBtn._wired = true;

  runBtn.addEventListener('click', runIdlingAnalysis);
  document.getElementById('idlingExportBtn')!.addEventListener('click', async () => {
    const { exportIdlingXLSX } = await import('./idling-export-xlsx');
    exportIdlingXLSX();
  });

  document.querySelectorAll<HTMLButtonElement>('.qr-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll<HTMLButtonElement>('.qr-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      idlingSetRange(btn.dataset.range!);
    });
  });

  const gsToggle = document.getElementById('idlingGselectorToggle')!;
  const gsPanel  = document.getElementById('idlingGselectorPanel')!;
  const vsToggle = document.getElementById('vselectorToggle')!;
  const vsPanel  = document.getElementById('vselectorPanel')!;

  gsToggle.addEventListener('click', () => {
    const opening = gsPanel.classList.contains('hidden');
    vsPanel.classList.add('hidden');
    gsPanel.classList.toggle('hidden');
    if (!opening) idlingResetGsSearch();
  });
  vsToggle.addEventListener('click', () => {
    const opening = vsPanel.classList.contains('hidden');
    gsPanel.classList.add('hidden');
    vsPanel.classList.toggle('hidden');
    if (!opening) idlingResetVsSearch();
  });
  document.addEventListener('click', (e) => {
    const gs = document.getElementById('idlingGselector');
    if (gs && !gs.contains(e.target as Node)) gsPanel.classList.add('hidden');
    const vs = document.getElementById('vselector');
    if (vs && !vs.contains(e.target as Node)) vsPanel.classList.add('hidden');
  });

  document.getElementById('idlingGselectAll')!.addEventListener('click', () => {
    document.querySelectorAll<HTMLInputElement>('#idlingGselectorList input[type="checkbox"]')
      .forEach((c) => { c.checked = true; });
    onGroupSelectionChange();
  });
  document.getElementById('idlingGselectNone')!.addEventListener('click', () => {
    document.querySelectorAll<HTMLInputElement>('#idlingGselectorList input[type="checkbox"]')
      .forEach((c) => { c.checked = false; });
    onGroupSelectionChange();
  });
  document.getElementById('idlingGsSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase().trim();
    document.querySelectorAll<HTMLElement>('#idlingGselectorList .vselector-item').forEach((row) => {
      const name = row.textContent?.toLowerCase().trim() ?? '';
      row.style.display = !q || name.includes(q) ? '' : 'none';
    });
  });

  document.getElementById('vsSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase().trim();
    document.querySelectorAll<HTMLElement>('#vselectorList .vselector-item').forEach(row => {
      const name = row.textContent?.toLowerCase().trim() ?? '';
      row.style.display = !q || name.includes(q) ? '' : 'none';
    });
    document.querySelectorAll<HTMLElement>('#vselectorList .vselector-group-hdr').forEach(hdr => {
      if (!q) { hdr.style.removeProperty('display'); return; }
      let next = hdr.nextElementSibling as HTMLElement | null;
      let visible = false;
      while (next && !next.classList.contains('vselector-group-hdr')) {
        if (next.style.display !== 'none') { visible = true; break; }
        next = next.nextElementSibling as HTMLElement | null;
      }
      hdr.style.display = visible ? '' : 'none';
    });
  });

  document.getElementById('vselectAll')!.addEventListener('click', idlingSelectAll);
  document.getElementById('vselectNone')!.addEventListener('click', idlingSelectNone);

  ['idlingFuelPrice', 'idlingCurrency', 'idlingUnit', 'idlingVehicleType'].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener('change', () => { if (idlingRawVehicleData.length) recalculateAndRender(); });
    el.addEventListener('input',  () => { if (idlingRawVehicleData.length) recalculateAndRender(); });
  });

  // Redraw canvas charts when theme toggles
  document.addEventListener('fleet-theme-change', () => {
    if (idlingPerVehicle.length) {
      const s = getIdlingSettings();
      renderIdlingCharts(s, idlingPerVehicle);
    }
  });

  discoverIdlingRules();
  discoverIdlingGroups();
}

function idlingResetVsSearch(): void {
  const inp = document.getElementById('vsSearch') as HTMLInputElement | null;
  if (inp) inp.value = '';
  document.querySelectorAll<HTMLElement>('#vselectorList .vselector-item, #vselectorList .vselector-group-hdr')
    .forEach(el => el.style.removeProperty('display'));
}

function idlingResetGsSearch(): void {
  const inp = document.getElementById('idlingGsSearch') as HTMLInputElement | null;
  if (inp) inp.value = '';
  document.querySelectorAll<HTMLElement>('#idlingGselectorList .vselector-item')
    .forEach(el => el.style.removeProperty('display'));
}

export function refreshIdlingCharts(): void {
  if (idlingPerVehicle.length) renderIdlingCharts(getIdlingSettings(), idlingPerVehicle);
}

export function resetIdlingPage(): void {
  idlingRawVehicleData     = [];
  idlingRawEvents          = [];
  idlingPerVehicle         = [];
  idlingDiscoveredRules    = [];
  idlingGroupLabelByDevice = new Map();
  idlingInitialized        = false;
  idlingGroupHierarchy     = null;

  const ruleSelect = document.getElementById('idlingRuleSelect') as (HTMLSelectElement & { _changeWired?: boolean }) | null;
  if (ruleSelect) {
    ruleSelect.innerHTML = '<option value="">Loading rules…</option>';
    ruleSelect._changeWired = false;
  }
  const ruleHint = document.getElementById('idlingRuleHint');
  if (ruleHint) ruleHint.textContent = '';

  const gselectorList = document.getElementById('idlingGselectorList');
  if (gselectorList) gselectorList.innerHTML = '';
  const gsLabel = document.getElementById('idlingGselectorLabel');
  if (gsLabel) gsLabel.textContent = 'Loading…';

  const idlingResults = document.getElementById('idlingResults');
  if (idlingResults) { idlingResults.classList.add('hidden'); idlingResults.style.display = ''; }

  document.getElementById('idlingEmpty')?.classList.remove('hidden');
  document.getElementById('idlingErrBox')?.classList.add('hidden');

  const exportBtn = document.getElementById('idlingExportBtn') as HTMLElement | null;
  if (exportBtn) exportBtn.style.display = 'none';

  const runBtn = document.getElementById('runIdlingBtn') as HTMLButtonElement & { _wired?: boolean } | null;
  if (runBtn) runBtn._wired = false;
}

// ── Rule discovery ────────────────────────────────────────────────────────

async function discoverIdlingRules(): Promise<void> {
  const select = document.getElementById('idlingRuleSelect') as HTMLSelectElement & { _changeWired?: boolean };
  const hint   = document.getElementById('idlingRuleHint') as HTMLElement;
  select.innerHTML = '<option value="">Searching for idling rules…</option>';
  select.disabled  = true;

  let rules: Rule[] = [];

  try {
    const allRules = await callGeotabAPI<Rule[]>('Get', { typeName: 'Rule', resultsLimit: 10000 });
    if (Array.isArray(allRules)) {
      const now = new Date();
      rules = allRules.filter((r) => {
        if (!r.name || !r.name.toLowerCase().includes('idl')) return false;
        if (r.activeTo && new Date(r.activeTo) < now) return false;
        return true;
      });
    }
  } catch (e1) {
    console.warn('Rule query failed, trying ExceptionEvent sample', e1);
    try {
      const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString();
      const sample    = await callGeotabAPI<ExceptionEvent[]>('Get', {
        typeName: 'ExceptionEvent',
        search: { fromDate: thirtyAgo },
        resultsLimit: 2000,
      });
      const seenIds: Record<string, Rule> = {};
      sample.forEach((ex) => {
        const r = ex.rule as Rule | undefined;
        if (r?.id && r.name && !seenIds[r.id]) seenIds[r.id] = r;
      });
      rules = Object.values(seenIds).filter((r) => r.name?.toLowerCase().includes('idl'));
      rules.forEach((r) => { r._discoveredViaSample = true; });
    } catch (e2) {
      console.warn('ExceptionEvent fallback also failed', e2);
    }
  }

  idlingDiscoveredRules = rules;

  if (!rules.length) {
    select.innerHTML = '<option value="">No active idling rules found</option>';
    hint.textContent =
      'No active idling rules were detected in this database. Ask your administrator to enable the Idling rule in MyGeotab → Administration → Exception Rules.';
    hint.style.color = 'var(--warning)';
    select.disabled  = false;
    return;
  }

  rules.sort((a, b) => {
    if (a.isReadOnly && !b.isReadOnly) return -1;
    if (!a.isReadOnly && b.isReadOnly) return 1;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });

  select.innerHTML = '';
  rules.forEach((r) => {
    const threshSec = extractThresholdFromRule(r);
    const threshStr = threshSec !== null ? ' · ' + fmtThreshold(threshSec) : '';
    const typeStr   = r.isReadOnly ? 'built-in' : (r._discoveredViaSample ? 'active' : 'custom');
    const opt       = document.createElement('option');
    opt.value       = r.id;
    opt.textContent = r.name + ' (' + typeStr + threshStr + ')';
    (opt as HTMLOptionElement & { dataset: DOMStringMap }).dataset.threshold = threshSec !== null ? String(threshSec) : '';
    (opt as HTMLOptionElement & { dataset: DOMStringMap }).dataset.isBuiltin  = r.isReadOnly ? '1' : '0';
    select.appendChild(opt);
  });
  select.disabled = false;
  updateRuleHint();

  if (!select._changeWired) {
    select.addEventListener('change', updateRuleHint);
    select._changeWired = true;
  }
}

function updateRuleHint(): void {
  const select = document.getElementById('idlingRuleSelect') as HTMLSelectElement | null;
  const hint   = document.getElementById('idlingRuleHint')   as HTMLElement | null;
  if (!select || !hint) return;
  const opt = select.options[select.selectedIndex] as HTMLOptionElement | null;
  if (!opt?.value) { hint.textContent = ''; return; }

  const isBuiltin = (opt as HTMLOptionElement & { dataset: DOMStringMap }).dataset.isBuiltin === '1';
  const threshStr = (opt as HTMLOptionElement & { dataset: DOMStringMap }).dataset.threshold;
  const threshSec = threshStr ? parseInt(threshStr) : null;

  const parts: string[] = [];
  parts.push(isBuiltin ? 'Built-in Geotab rule' : 'Custom rule');
  if (threshSec !== null) parts.push('Triggers after ' + fmtThreshold(threshSec) + ' of idling');
  parts.push('Data comes from ExceptionEvent API — matches Geotab reports exactly');
  hint.textContent = parts.join(' · ');
  hint.style.color = '';
}

// ── Group filter ──────────────────────────────────────────────────────────
// A separate hierarchical Group control (mirrors the Safety Scorecard page) so a fleet can be
// scoped to one or more Groups without hand-checking every vehicle. Toggling a group checks/
// unchecks every vehicle in that group (and its sub-groups) in the Vehicle selector below —
// which stays the single source of truth for Run Analysis — so per-vehicle overrides are safe
// to make afterward, but are re-synced to match whenever a group checkbox changes again.

async function discoverIdlingGroups(): Promise<void> {
  const list = document.getElementById('idlingGselectorList') as HTMLElement | null;
  if (!list) return;
  list.innerHTML = '<div class="vselector-item" style="color:var(--muted)">Loading groups…</div>';

  let rawGroups: Group[] = [];
  try {
    rawGroups = await callGeotabAPI<Group[]>('Get', { typeName: 'Group', resultsLimit: 1000 });
  } catch (e) {
    console.warn('Group query failed', e);
  }

  idlingGroupHierarchy = buildGroupHierarchy(rawGroups);
  populateGroupSelector();
}

function deviceGroupIds(v: (typeof state.allVehicles)[number]): string[] {
  const ids = (v.device.groups ?? []).map((g) => g.id).filter((id) => !isSystemGroupId(id));
  return ids.length ? ids : ['__none__'];
}

function populateGroupSelector(): void {
  const list = document.getElementById('idlingGselectorList');
  if (!list) return;
  list.innerHTML = '';

  const hasUngrouped = state.allVehicles.some((v) => deviceGroupIds(v).includes('__none__'));
  const nodes = idlingGroupHierarchy?.groups ?? [];

  if (!nodes.length && !hasUngrouped) {
    list.innerHTML = '<div style="padding:0.4rem 0.75rem;font-size:0.65rem;color:var(--muted)">No groups found</div>';
    const label = document.getElementById('idlingGselectorLabel');
    if (label) label.textContent = 'No Groups';
    return;
  }

  const addRow = (id: string, name: string, depth: number): void => {
    const lbl = document.createElement('label');
    lbl.className = 'vselector-item';
    if (depth > 0) lbl.style.paddingLeft = `${0.75 + depth * 0.85}rem`;
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = true;
    cb.value = id;
    cb.addEventListener('change', onGroupSelectionChange);
    const nm = document.createElement('span');
    nm.className = 'vselector-item-name';
    nm.textContent = name;
    lbl.appendChild(cb);
    lbl.appendChild(nm);
    list.appendChild(lbl);
  };

  nodes.forEach(({ id, name, depth }) => addRow(id, name, depth));
  if (hasUngrouped) addRow('__none__', 'Ungrouped', 0);

  updateGroupSelectorLabel();
}

function updateGroupSelectorLabel(): void {
  const all     = document.querySelectorAll('#idlingGselectorList input[type="checkbox"]').length;
  const checked = document.querySelectorAll('#idlingGselectorList input[type="checkbox"]:checked').length;
  const label = document.getElementById('idlingGselectorLabel');
  if (!label) return;
  label.textContent = all === 0 ? 'No Groups' : checked === all ? `All Groups (${all})` : `${checked} / ${all} groups`;
}

interface GroupFilterSelection {
  checked:  Set<string>;  // exactly the boxes ticked in the filter panel
  expanded: Set<string>;  // checked, plus every descendant of a checked parent
}

function getGroupFilterSelection(): GroupFilterSelection {
  const checked = new Set(
    Array.from(document.querySelectorAll<HTMLInputElement>('#idlingGselectorList input:checked')).map((c) => c.value)
  );
  const expanded = expandGroups(checked, idlingGroupHierarchy?.groupChildren ?? new Map());
  return { checked, expanded };
}

function onGroupSelectionChange(): void {
  updateGroupSelectorLabel();

  const { expanded } = getGroupFilterSelection();
  const devMap = new Map(state.allVehicles.map((v) => [v.device.id, v]));
  document.querySelectorAll<HTMLInputElement>('#vselectorList input[type="checkbox"]').forEach((cb) => {
    const devId = (cb as HTMLInputElement & { dataset: DOMStringMap }).dataset.devId!;
    const v = devMap.get(devId);
    const gids = v ? deviceGroupIds(v) : ['__none__'];
    cb.checked = gids.some((g) => expanded.has(g));
  });
  updateVehicleSelectorLabel();
}

// The Group column in the results table should show WHY a vehicle matched the active Group
// filter, not an arbitrary one of its (possibly several, cross-hierarchy) group memberships —
// a vehicle can sit in unrelated branches simultaneously (e.g. a region code, a technician name,
// and a depot number), and only one of those may actually be under the group(s) selected above.
function matchedGroupLabel(v: (typeof state.allVehicles)[number], expanded: Set<string>): string {
  const ids = deviceGroupIds(v);
  if (ids[0] === '__none__') return 'Ungrouped';
  const matched = ids.filter((id) => expanded.has(id));
  const names = (matched.length ? matched : ids).map((id) => idlingGroupHierarchy?.groupMap.get(id) ?? id);
  return names.sort((a, b) => a.localeCompare(b)).join(', ');
}

// The "Idle Time by Group" chart/sheet is an aggregate across every vehicle, so leaf-level detail
// (which is exactly right for the per-vehicle table above) explodes into dozens of one-off
// combinations when many vehicles each sit in different cross-hierarchy leaves. Roll each matched
// leaf up to whatever level the user actually checked in the filter — checking only a top-level
// parent buckets every descendant vehicle under that one name; checking a narrower sub-group
// keeps the breakdown at that finer level instead.
//
// A vehicle can genuinely belong to more than one top-level tree at once (e.g. both "Tennant" and
// "Tennant Vehicles" are real, independent org divisions, not one shared hierarchy). Rather than
// merging those into a combined "A, B" bucket, this returns every matched root separately — the
// chart/sheet then adds the vehicle's hours to EACH one, so every group's own total is complete
// (it includes vehicles that also belong elsewhere) and no combined bucket ever appears. The
// trade-off: totals across groups can add up to more than the fleet total, since an overlapping
// vehicle is counted more than once — that's an accurate reflection of the overlap, not a bug.
function rootGroupLabels(v: (typeof state.allVehicles)[number], selection: GroupFilterSelection): string[] {
  const ids = deviceGroupIds(v);
  if (ids[0] === '__none__') return ['Ungrouped'];
  const matched  = ids.filter((id) => selection.expanded.has(id));
  const relevant = matched.length ? matched : ids;
  const parentOf = idlingGroupHierarchy?.parentOf ?? new Map<string, string>();
  const rolled   = new Set(relevant.map((id) => shallowestSelectedAncestor(id, selection.checked, parentOf)));
  const names    = Array.from(rolled).map((id) => idlingGroupHierarchy?.groupMap.get(id) ?? id);
  return names.sort((a, b) => a.localeCompare(b));
}

// ── Date range ────────────────────────────────────────────────────────────

function idlingSetRange(range: string): void {
  const now  = new Date();
  let from   = new Date();
  const to   = new Date();
  to.setHours(23, 59, 59, 999);

  if (range === '7d')  from = new Date(Date.now() - 7  * 86400000);
  if (range === '30d') from = new Date(Date.now() - 30 * 86400000);
  if (range === 'mtd') from = new Date(now.getFullYear(), now.getMonth(), 1);
  if (range === 'ytd') from = new Date(now.getFullYear(), 0, 1);
  from.setHours(0, 0, 0, 0);

  (document.getElementById('idlingFrom') as HTMLInputElement).value = from.toISOString().slice(0, 10);
  (document.getElementById('idlingTo')   as HTMLInputElement).value = to.toISOString().slice(0, 10);
}

// ── Vehicle selector ──────────────────────────────────────────────────────

export function buildVehicleSelector(): void {
  const list = document.getElementById('vselectorList')!;
  list.innerHTML = '';

  const byGroup: Record<string, typeof state.allVehicles> = {};
  state.allVehicles.forEach((v) => {
    const g = v.group || 'No Group';
    if (!byGroup[g]) byGroup[g] = [];
    byGroup[g].push(v);
  });

  const groupNames = Object.keys(byGroup).sort((a, b) => {
    if (a === 'No Group') return 1;
    if (b === 'No Group') return -1;
    return a.localeCompare(b);
  });

  groupNames.forEach((gName) => {
    const hdr = document.createElement('div');
    hdr.className = 'vselector-group-hdr';
    hdr.textContent = gName + ' (' + byGroup[gName].length + ')';
    list.appendChild(hdr);

    byGroup[gName].forEach((v) => {
      const row = document.createElement('label');
      row.className = 'vselector-item';
      const cb  = document.createElement('input');
      cb.type    = 'checkbox';
      cb.checked = true;
      (cb as HTMLInputElement & { dataset: DOMStringMap }).dataset.devId = v.device.id;
      cb.addEventListener('change', updateVehicleSelectorLabel);
      const nm = document.createElement('span');
      nm.className = 'vselector-item-name';
      nm.textContent = v.device.name;
      row.appendChild(cb);
      row.appendChild(nm);
      list.appendChild(row);
    });
  });
  updateVehicleSelectorLabel();
}

function updateVehicleSelectorLabel(): void {
  const all     = document.querySelectorAll<HTMLInputElement>('#vselectorList input[type="checkbox"]');
  const checked = Array.from(all).filter((c) => c.checked).length;
  const lbl     = document.getElementById('vselectorLabel');
  if (lbl) {
    lbl.textContent = checked === all.length
      ? 'All ' + all.length + ' vehicles'
      : checked + ' of ' + all.length + ' vehicles';
  }
}

function idlingSelectAll(): void {
  document.querySelectorAll<HTMLInputElement>('#vselectorList input').forEach((c) => { c.checked = true; });
  updateVehicleSelectorLabel();
}

function idlingSelectNone(): void {
  document.querySelectorAll<HTMLInputElement>('#vselectorList input').forEach((c) => { c.checked = false; });
  updateVehicleSelectorLabel();
}

function getSelectedDeviceIds(): Set<string> {
  return new Set(
    Array.from(document.querySelectorAll<HTMLInputElement>('#vselectorList input:checked')).map(
      (c) => (c as HTMLInputElement & { dataset: DOMStringMap }).dataset.devId!
    )
  );
}

// ── Run analysis ──────────────────────────────────────────────────────────

async function runIdlingAnalysis(): Promise<void> {
  const fromVal = (document.getElementById('idlingFrom') as HTMLInputElement).value;
  const toVal   = (document.getElementById('idlingTo')   as HTMLInputElement).value;
  if (!fromVal || !toVal) { alert('Please select a date range.'); return; }

  const ruleSelect    = document.getElementById('idlingRuleSelect') as HTMLSelectElement;
  const selectedRuleId = ruleSelect?.value ?? '';
  if (!selectedRuleId) {
    alert('No idling rule selected. Please wait for rule discovery or check that an idling rule is active in your database.');
    return;
  }

  const selectedIds = getSelectedDeviceIds();
  if (!selectedIds.size) { alert('Select at least one vehicle.'); return; }

  const s   = getIdlingSettings();
  const btn = document.getElementById('runIdlingBtn') as HTMLButtonElement;
  btn.disabled  = true;
  btn.innerHTML = '<span style="opacity:0.6">Loading…</span>';

  document.getElementById('idlingEmpty')!.classList.add('hidden');
  document.getElementById('idlingResults')!.classList.add('hidden');
  document.getElementById('idlingErrBox')!.classList.add('hidden');
  document.getElementById('idlingWarnBox')!.classList.add('hidden');
  document.getElementById('idlingLoading')!.classList.remove('hidden');

  const loadingLabel = document.getElementById('idlingLoadingLabel');
  if (loadingLabel) loadingLabel.textContent = 'Fetching exception events…';

  try {
    const from = new Date(fromVal + 'T00:00:00');
    const to   = new Date(toVal   + 'T23:59:59');

    // Windowed fetch: any 50k-capped window is split in half until complete, so a long range
    // (30d / YTD) or a busy idling rule no longer silently truncates.
    let capHit = false;
    const allExceptions = await fetchRangeChunked<ExceptionEvent>({
      fromISO: from.toISOString(),
      toISO:   to.toISOString(),
      perCallLimit: 50000,
      fetch: (fromISO, toISO) => callGeotabAPI<ExceptionEvent[]>('Get', {
        typeName: 'ExceptionEvent',
        search: { fromDate: fromISO, toDate: toISO, ruleSearch: { id: selectedRuleId } },
        resultsLimit: 50000,
      }),
      onCap: () => { capHit = true; },
    });

    if (capHit) {
      const wb = document.getElementById('idlingWarnBox')!;
      wb.textContent = '⚠ A single hour logged over 50,000 idling events — that window may be incomplete.';
      wb.classList.remove('hidden');
    }

    // When all tracked vehicles are selected, include events from every device
    // (deactivated/untracked vehicles still generate exceptions in Geotab — filtering
    // them out causes the FleetLume By EnVue count to be lower than Geotab's own report).
    const allSelected = selectedIds.size >= state.allVehicles.length;
    const exceptions  = allSelected
      ? allExceptions
      : allExceptions.filter((ex) => selectedIds.has(ex.device?.id));
    idlingRawEvents = exceptions;

    const devMap = new Map<string, (typeof state.allVehicles)[0]>();
    state.allVehicles.forEach((v) => devMap.set(v.device.id, v));

    // Group column: which of the vehicle's own (possibly cross-hierarchy) group memberships
    // actually fall under the Group filter currently selected above. groupRollups is the coarser
    // version used by the "Idle Time by Group" chart/sheet (see rootGroupLabels for why).
    const groupSelection = getGroupFilterSelection();

    idlingGroupLabelByDevice = new Map();

    const byVehicle: Record<string, { name: string; group: string; groupsLabel: string; groupRollups: string[]; eventCount: number; totalIdleSec: number; longestIdleSec: number }> = {};
    exceptions.forEach((ex) => {
      const devId = ex.device?.id;
      if (!devId) return;
      const vData = devMap.get(devId);
      if (!byVehicle[devId]) {
        const groupsLabel = vData ? matchedGroupLabel(vData, groupSelection.expanded) : 'Untracked';
        byVehicle[devId] = {
          name:          vData ? vData.device.name : devId,
          group:         vData ? vData.group : 'Untracked',
          groupsLabel,
          groupRollups:  vData ? rootGroupLabels(vData, groupSelection) : ['Untracked'],
          eventCount:    0,
          totalIdleSec:  0,
          longestIdleSec: 0,
        };
        idlingGroupLabelByDevice.set(devId, groupsLabel);
      }
      const idleSec = parseTimespanToSec(ex.duration);
      byVehicle[devId].eventCount++;
      byVehicle[devId].totalIdleSec  += idleSec;
      byVehicle[devId].longestIdleSec = Math.max(byVehicle[devId].longestIdleSec, idleSec);
    });

    idlingRawVehicleData = Object.values(byVehicle).map((v) => ({
      name:          v.name,
      group:         v.group,
      groupsLabel:   v.groupsLabel,
      groupRollups:  v.groupRollups,
      eventCount:    v.eventCount,
      idleHrs:       v.totalIdleSec / 3600,
      longestIdleHrs: v.longestIdleSec / 3600,
      pctDrive:      0,
    }));

    document.getElementById('idlingLoading')!.classList.add('hidden');

    const ruleOpt  = ruleSelect.options[ruleSelect.selectedIndex];
    const ruleName = ruleOpt ? ruleOpt.textContent : 'Idling';
    const vehicleLabel = allSelected
      ? state.allVehicles.length + ' vehicles'
      : selectedIds.size + ' of ' + state.allVehicles.length + ' vehicles';
    (document.getElementById('idlingMeta') as HTMLElement).textContent =
      from.toLocaleDateString() + ' – ' + to.toLocaleDateString() +
      ' · Rule: ' + ruleName +
      ' · ' + vehicleLabel +
      ' · ' + exceptions.length.toLocaleString() + ' idle events';

    const resultsEl = document.getElementById('idlingResults')!;
    resultsEl.classList.remove('hidden');
    resultsEl.style.display = 'flex';
    (document.getElementById('idlingExportBtn') as HTMLElement).style.display = '';

    if (!idlingRawVehicleData.length) {
      document.getElementById('idlingKpiGrid')!.innerHTML =
        '<div style="grid-column:1/-1;text-align:center;padding:2rem;color:var(--muted);font-size:0.82rem;">No idle events found for the selected date range, vehicles, and rule. Check that the rule is active and vehicles are assigned to its group in MyGeotab.</div>';
      (document.getElementById('idlingTable') as HTMLElement).innerHTML = '';
      (document.getElementById('idlingRowCount') as HTMLElement).textContent = '0 vehicles';
      return;
    }

    recalculateAndRender();

  } catch (err) {
    document.getElementById('idlingLoading')!.classList.add('hidden');
    (document.getElementById('idlingErrBox') as HTMLElement).textContent = 'Error: ' + (err as Error).message;
    document.getElementById('idlingErrBox')!.classList.remove('hidden');
    document.getElementById('idlingEmpty')!.classList.remove('hidden');
  } finally {
    btn.disabled  = false;
    btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><polygon points="3,1 13,7 3,13" fill="currentColor"/></svg> Run Analysis';
  }
}

// ── Recalculate ───────────────────────────────────────────────────────────

export function recalculateAndRender(): void {
  if (!idlingRawVehicleData.length) return;
  const s = getIdlingSettings();
  idlingPerVehicle = idlingRawVehicleData.map((v) => {
    const fuelVol = volumeInUnit(v.idleHrs, s.burnRateL, s.unit);
    return { ...v, fuelVol, cost: fuelVol * s.fuelPrice };
  });
  renderIdlingKPIs(s, idlingPerVehicle);
  renderIdlingCharts(s, idlingPerVehicle);
  renderIdlingTable(s, idlingPerVehicle, idlingSortCol, idlingSortAsc);
}
